import type { ReactNode } from 'react';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import { SectionHeading, tone } from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';
import { SPEAKER_COLORS } from '@/components/audio/audioUi';

import { SpeakerDot, useLandingT } from './kit';

/**
 * What comes with every transcript, each shown as a small piece of the app
 * rather than an icon: speakers, AI notes, chat, subtitles and languages.
 */
export default function Capabilities() {
  const { t } = useLandingT();
  const reveal = useReveal<HTMLDivElement>(0.15);

  return (
    <section className='py-20 sm:py-28'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <SectionHeading
          title={t('More than a transcript.')}
          tail={t('Everything you need after it.')}
          lead={t(
            'Every transcript comes with the tools to use it: notes written for you, answers about what was said, subtitles and a link to share.',
          )}
        />

        <div
          ref={reveal.ref}
          data-reveal={reveal.state}
          className={clsxm(
            styles.reveal,
            'mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-6',
          )}
        >
          <Tile
            className='sm:col-span-2 lg:col-span-4'
            delayMs={0}
            title={t('Who said what, and when')}
            body={t(
              'Speakers are told apart for you and every line has a timestamp. Give each speaker a name and the transcript shows it on every line.',
            )}
          >
            <SpeakersVisual />
          </Tile>
          <Tile
            className='lg:col-span-2'
            delayMs={90}
            title={t('Notes written for you')}
            body={t(
              'Summaries, action items, show notes, articles, social posts and more, written from your recording.',
            )}
          >
            <NotesVisual />
          </Tile>
          <Tile
            className='lg:col-span-2'
            delayMs={180}
            title={t('Ask your recording')}
            body={t(
              'Chat with the transcript to find a decision, a quote or a number without scrolling.',
            )}
          >
            <ChatVisual />
          </Tile>
          <Tile
            className='lg:col-span-2'
            delayMs={270}
            title={t('Subtitles in one click')}
            body={t(
              'Download SRT or VTT files for YouTube, Premiere and most video editors.',
            )}
          >
            <SubtitlesVisual />
          </Tile>
          <Tile
            className='lg:col-span-2'
            delayMs={360}
            title={t('45 languages')}
            body={t(
              'Pick the language your recording is in, or let it be detected for you.',
            )}
          >
            <LanguagesVisual />
          </Tile>
        </div>
      </div>
    </section>
  );
}

function Tile({
  title,
  body,
  children,
  className,
  delayMs,
}: {
  title: string;
  body: string;
  children: ReactNode;
  className?: string;
  delayMs: number;
}) {
  return (
    <div
      className={clsxm(
        styles.rv,
        'flex flex-col overflow-hidden rounded-[28px] bg-white ring-1 ring-[#0f0f28]/[0.07] dark:bg-[#14141f] dark:ring-white/[0.08]',
        className,
      )}
      style={{ '--d': `${delayMs}ms` } as React.CSSProperties}
    >
      <div
        aria-hidden='true'
        className='relative flex min-h-[176px] flex-1 items-center justify-center bg-[#f5f5fb] px-5 py-6 dark:bg-white/[0.03]'
      >
        {children}
      </div>
      <div className='p-6'>
        <h3 className={clsxm('text-lg font-semibold tracking-tight', tone.ink)}>
          {title}
        </h3>
        <p className={clsxm('mt-2 text-[15px] leading-7', tone.body)}>{body}</p>
      </div>
    </div>
  );
}

function SpeakersVisual() {
  const { t } = useLandingT();
  const rows = [
    { name: 'Maya', time: '0:04', text: t('Quick update before we start.') },
    {
      name: 'Daniel',
      time: '0:09',
      text: t('The beta opens to everyone on Monday.'),
    },
    { name: 'Sofia', time: '0:15', text: t('Support is ready for it.') },
  ];
  return (
    <div className='grid w-full max-w-xl gap-2.5 sm:grid-cols-[1fr_auto] sm:items-center sm:gap-6'>
      <ol className='space-y-2'>
        {rows.map((row, index) => (
          <li
            key={row.name}
            className='flex items-start gap-3 rounded-2xl bg-white px-3.5 py-2.5 shadow-[0_1px_2px_rgba(15,15,40,0.05)] ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/[0.06]'
          >
            <SpeakerDot color={SPEAKER_COLORS[index]} className='mt-[7px]' />
            <div className='min-w-0 text-[13px]'>
              <p className='flex items-center gap-2'>
                <span className={clsxm('font-semibold', tone.ink)}>
                  {row.name}
                </span>
                <span className={clsxm('tabular-nums', tone.quiet)}>
                  {row.time}
                </span>
              </p>
              <p className={clsxm('truncate', tone.body)}>{row.text}</p>
            </div>
          </li>
        ))}
      </ol>
      <div className='hidden rounded-2xl bg-white p-3.5 text-[12px] shadow-[0_1px_2px_rgba(15,15,40,0.05)] ring-1 ring-black/[0.04] sm:block dark:bg-white/[0.06] dark:ring-white/[0.06]'>
        <p className={clsxm('font-semibold', tone.ink)}>{t('Speakers')}</p>
        {['Maya', 'Daniel', 'Sofia'].map((name, index) => (
          <p
            key={name}
            className={clsxm('mt-2 flex items-center gap-2', tone.body)}
          >
            <span className='w-4 text-[11px] font-semibold text-[#8a8aa1]'>
              {String.fromCharCode(65 + index)}
            </span>
            <span className='rounded-md bg-[#f3f3f7] px-2 py-0.5 dark:bg-white/[0.08]'>
              {name}
            </span>
          </p>
        ))}
      </div>
    </div>
  );
}

function NotesVisual() {
  const { t } = useLandingT();
  const items = [
    t('Summary'),
    t('Action Items'),
    t('Show Notes'),
    t('Timestamped Overview'),
    t('Newsletter'),
    t('Linkedin Post'),
  ];
  return (
    <div className='flex max-w-[16rem] flex-wrap justify-center gap-1.5'>
      {items.map((item, index) => (
        <span
          key={item}
          className={clsxm(
            'rounded-full px-3 py-1.5 text-xs font-semibold',
            index === 0
              ? 'bg-primary-600 text-white'
              : clsxm(
                  'bg-white ring-1 ring-black/[0.06] dark:bg-white/[0.07] dark:ring-white/10',
                  tone.ink,
                ),
          )}
        >
          {item}
        </span>
      ))}
    </div>
  );
}

function ChatVisual() {
  const { t } = useLandingT();
  return (
    <div className='w-full max-w-[17rem] space-y-2 text-[12.5px] leading-snug'>
      <p className='ms-auto w-fit max-w-[85%] rounded-2xl rounded-ee-md bg-primary-600 px-3 py-2 text-white'>
        {t('What did we decide about pricing?')}
      </p>
      <p
        className={clsxm(
          'w-fit max-w-[92%] rounded-2xl rounded-es-md bg-white px-3 py-2 ring-1 ring-black/[0.05] dark:bg-white/[0.07] dark:ring-white/10',
          tone.ink,
        )}
      >
        {t('Keep the current price and test a yearly discount in May.')}{' '}
        <span className='font-semibold tabular-nums text-primary-600 dark:text-primary-400'>
          12:41
        </span>
      </p>
    </div>
  );
}

function SubtitlesVisual() {
  const { t } = useLandingT();
  const cues = [
    {
      range: '00:00:04,120 --> 00:00:07,480',
      text: t('Welcome back to the channel.'),
    },
    {
      range: '00:00:07,600 --> 00:00:10,900',
      text: t('Today we test three cameras.'),
    },
  ];
  return (
    <div className='w-full max-w-[17rem] rounded-2xl bg-white p-3.5 font-mono text-[11px] leading-relaxed shadow-[0_1px_2px_rgba(15,15,40,0.05)] ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:ring-white/[0.06]'>
      <p className='mb-1.5 font-mono text-[11px] font-semibold text-primary-600 dark:text-primary-400'>
        captions.srt
      </p>
      {cues.map((cue, index) => (
        <div key={cue.range} className={index ? 'mt-2' : undefined}>
          <p className={tone.quiet}>{index + 1}</p>
          <p className={tone.quiet}>{cue.range}</p>
          <p className={clsxm('font-sans text-[12px]', tone.ink)}>{cue.text}</p>
        </div>
      ))}
    </div>
  );
}

function LanguagesVisual() {
  const { t } = useLandingT();
  const languages = [
    'English',
    'Spanish',
    'French',
    'German',
    'Japanese',
    'Hindi',
    'Arabic',
    'Portuguese',
  ];
  return (
    <div className='flex max-w-[16rem] flex-wrap justify-center gap-1.5'>
      {languages.map((language, index) => (
        <span
          key={language}
          className={clsxm(
            'rounded-full px-3 py-1.5 text-xs font-medium ring-1',
            index === 0
              ? 'bg-emerald-50 text-emerald-700 ring-emerald-200 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/20'
              : clsxm(
                  'bg-white ring-black/[0.06] dark:bg-white/[0.07] dark:ring-white/10',
                  tone.body,
                ),
          )}
        >
          {t(language)}
        </span>
      ))}
    </div>
  );
}
